/**
 * Dataset workspace API calls (specs/dataset-workspace.md). Errors carry the server's message.
 * File bytes travel straight to object storage with presigned URLs (#973); only
 * the file's bucket key goes through the API.
 */
import API_ENDPOINTS from '../../../config/api';
import { uploadFilesToStorage, uploadFileToStorage } from '../../../utils/storageUpload';

async function request(url, options = {}) {
  const res = await fetch(url, { credentials: 'include', ...options });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const json = (method, payload) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload || {}),
});

export const listDatasets = () => request(API_ENDPOINTS.ADMIN_DATASETS()).then((b) => b.datasets || []);
export const createDataset = (payload) => request(API_ENDPOINTS.ADMIN_DATASETS(), json('POST', payload));
export const getDataset = (id) => request(API_ENDPOINTS.ADMIN_DATASET(id));
/** Changing is_global answers 409 with `needsConfirm` and `impact` unless confirmGlobal is set. */
export const updateDataset = (id, patch) => request(API_ENDPOINTS.ADMIN_DATASET(id), json('PATCH', patch));
export const bulkSources = (id, action, ids) => request(API_ENDPOINTS.ADMIN_DATASET_SOURCES_BULK(id), json('POST', { action, ids }));
/** Indexed documents no workspace source owns (indexed with the CLI), read-only (#891). */
export const getTagSourcesCount = (id) => request(API_ENDPOINTS.ADMIN_DATASET_TAG_SOURCES(id));

/**
 * Tag the dataset's untagged sources: the admin-only `knowledge.tag_sources` job
 * (specs/knowledge-kinds.md, step 6). It belongs to no instance, so it is polled here, as
 * opinionApi.buildProfiles polls its job. Resolves with the job's result; onStage gets the
 * stage key (`tag 12/71`, `headers`).
 */
export async function tagSources(datasetId, { onStage = () => {}, intervalMs = 2000 } = {}) {
  const { jobId } = await request(API_ENDPOINTS.AI_JOBS(), json('POST', { type: 'knowledge.tag_sources', input: { datasetId } }));
  for (;;) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const job = await request(API_ENDPOINTS.AI_JOB(jobId));
    if (job.stage) onStage(job.stage);
    if (job.status === 'done') return job.result;
    if (job.status === 'failed' || job.status === 'cancelled') throw new Error(job.error || `Job ${job.status}`);
  }
}

export const listIndexedDocuments = (id) => request(API_ENDPOINTS.ADMIN_DATASET_INDEXED_DOCUMENTS(id)).then((b) => b.documents || []);
// A command-line document's kind (fact / policy / opinion / null) or exclusion from search (#1382)
export const updateIndexedDocument = (id, docId, patch) => request(API_ENDPOINTS.ADMIN_DATASET_INDEXED_DOCUMENT(id, docId), json('PATCH', patch));
export const listSources = (id) => request(API_ENDPOINTS.ADMIN_DATASET_SOURCES(id)).then((b) => b.sources || []);
export const getSource = (id, sid) => request(API_ENDPOINTS.ADMIN_DATASET_SOURCE(id, sid));
export const updateSource = (id, sid, patch) => request(API_ENDPOINTS.ADMIN_DATASET_SOURCE(id, sid), json('PATCH', patch));
export const deleteSource = (id, sid) => request(API_ENDPOINTS.ADMIN_DATASET_SOURCE(id, sid), { method: 'DELETE' });
export const sourceAction = (id, sid, action, payload) =>
  request(API_ENDPOINTS.ADMIN_DATASET_SOURCE_ACTION(id, sid, action), json('POST', payload));

const fileFields = (up) => (up
  ? { fileKey: up.key, filename: up.name, size: up.size, sha256: up.sha256, fileType: up.type }
  : {});

export async function addSource(id, { title, publisher, link, file }, { onProgress } = {}) {
  const up = await uploadFileToStorage(API_ENDPOINTS.ADMIN_DATASET_UPLOAD_URLS(id), file, { onProgress });
  return request(API_ENDPOINTS.ADMIN_DATASET_SOURCES(id), json('POST', { title, publisher, link, ...fileFields(up) }));
}

/** PDFs and ZIPs of PDFs, each PDF a new source (#891). Answers { added, skipped, failed }. */
export async function uploadPdfs(id, files, { onProgress } = {}) {
  const uploaded = await uploadFilesToStorage(API_ENDPOINTS.ADMIN_DATASET_UPLOAD_URLS(id), files, { onProgress });
  return request(API_ENDPOINTS.ADMIN_DATASET_UPLOADS_COMPLETE(id), json('POST', { files: uploaded }));
}

export async function uploadSourceFile(id, sid, file, { onProgress } = {}) {
  const up = await uploadFileToStorage(API_ENDPOINTS.ADMIN_DATASET_UPLOAD_URLS(id), file, { onProgress });
  return request(API_ENDPOINTS.ADMIN_DATASET_SOURCE_FILE(id, sid), json('POST', fileFields(up)));
}

// Who may do what with the dataset (specs/access-model.md). Listing answers 403
// for anyone below manage; the settings panel hides the section on that.
export const listAccess = (id) => request(API_ENDPOINTS.ADMIN_DATASET_ACCESS(id)).then((b) => b.access || []);
export const grantDatasetAccess = (id, email, action) => request(API_ENDPOINTS.ADMIN_DATASET_ACCESS(id), json('PUT', { email, action }));
export const revokeDatasetAccess = (id, userId) => request(API_ENDPOINTS.ADMIN_DATASET_ACCESS_USER(id, userId), { method: 'DELETE' });

// A failed source's label: the error code the worker stored, else a plain "error".
export function errorKey(source) {
  const code = source.metadata?.errorCode;
  return `datasetWorkspace.errors.${code === 'download_blocked' || code === 'download_failed' ? code : 'generic'}`;
}

// Statuses the worker is still moving; the workspace polls while any source is in one.
export const ACTIVE_STATUSES = new Set(['pending_download', 'pending_conversion', 'approved', 'indexing']);
export const REVIEWABLE_STATUSES = new Set(['pending_review', 'needs_review']);
export const LOCKED_STATUSES = new Set(['approved', 'indexing', 'indexed']);
// What a source is, read from it at conversion (#891); labels in datasetWorkspace.kinds.*.
export const SOURCE_KINDS = ['fact', 'policy', 'opinion', 'testimony'];

// Failed downloads are taken to be bot blockers: a person downloads the file by hand.
export const needsManualDownload = (s) => s.status === 'failed' && !s.has_file
  && ['download_blocked', 'download_failed'].includes(s.metadata?.errorCode);
// The profile made a web search link because the PDF named no URL of its own.
export const isSearchLink = (s) => s.metadata?.profile?.linkFrom === 'search';
